// Display names for the values the investments data stores. The stored value
// stays the key everywhere — filters, URL state, colours, clustering, CSV joins
// — and only what a reader sees changes.
//
// Data names (federal states, WZ industries) are translated through the
// i18n.translations table (data.ts; docs/i18n.md). The code-defined enums
// (investment types, statuses) use the dictionaries de.ts / en.ts.
import { getLang } from './lang';
import { de } from './de';
import { en } from './en';
import { dataLabel } from './data';

/** "Bayern" → "Bavaria" in English; unchanged in German or when unknown. */
export function stateLabel(state: string): string {
  return dataLabel('common.federal_state', state);
}

/** The API's catch-all bucket for rows without a value (db.ts 'Sonstige'). */
export function otherLabel(value: string): string {
  return getLang() === 'en' && (value === 'Sonstige' || value === 'Sonstiges') ? 'Other' : value;
}

/** Stored type_of_investment → display name: "Greenfield expansion" →
    "Greenfield-Investition" / "Greenfield expansion". The 'Sonstige' bucket
    goes through otherLabel; IPO, Funding, M&A and unknown values as stored. */
export function typeLabel(value: string): string {
  const m = (getLang() === 'en' ? en : de).investmentTypes;
  if (value === 'Greenfield expansion') return m.greenfield;
  if (value === 'Brownfield expansion') return m.brownfield;
  return otherLabel(value);
}

/** Stored investment_status ("announced", "in progress", "finished",
    "cancelled") → display name in either language; unknown values as stored. */
export function statusLabel(value: string): string {
  const m = (getLang() === 'en' ? en : de).investmentStatuses;
  switch (value) {
    case 'announced': return m.announced;
    case 'in progress': return m.inProgress;
    case 'finished': return m.finished;
    case 'cancelled': return m.cancelled;
    default: return value;
  }
}

// --- Industries -------------------------------------------------------------
// Stored as "<WZ code> - <German name>"; investments.industry is keyed by the
// code. Without a translation, or for a label without a code, the German
// label shows — never a blank.

/** "62 - Erbringung von …" → "62 - Computer programming, …" in English. */
export function industryLabel(label: string): string {
  const sep = label.indexOf(' - ');
  if (sep <= 0) return otherLabel(label);
  const code = label.slice(0, sep);
  const translated = dataLabel('investments.industry', code);
  return translated === code ? label : `${code} - ${translated}`;
}
